import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  FormControlLabel,
  Switch,
  Tooltip,
  Button,
  Alert
} from '@mui/material';
import { apiService } from '@/services/api';
import { useFilters } from '@/contexts/FilterContext';
import { useRefresh } from '@/contexts/RefreshContext';
import { StatusToggle } from '@/components/StatusToggle';
import { ClientCell } from '@/components/ClientCell';
import { useConfirmation } from '@/contexts/ConfirmationContext';
import { useMessage } from '@/contexts/MessageContext';
import { logger } from '@/utils/logger';
import { COPY } from '@/config/copy';
import { ProductScopeGate } from '@/components/ProductScopeGate';
import { DEFAULT_PRODUCT_ID } from '@/config/constants';
import { useProductsMap } from '@/hooks/useProductsMap';
import { TenantPricingDialog } from '@/components/platform/TenantPricingDialog';

export const TenantsPage: React.FC = () => {
  const [tenants, setTenants] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const { selectedClients, productId } = useFilters();
  const { setRefreshCallback } = useRefresh();
  const { confirm } = useConfirmation();
  const { showSuccess, showError } = useMessage();
  const [creatingTestTenant, setCreatingTestTenant] = useState(false);

  // Coluna Parceiro visível apenas para produtos que não são o vover-app (DEFAULT_PRODUCT_ID)
  const hasPartnerFeature = !!productId && productId !== DEFAULT_PRODUCT_ID;
  // Preço por cliente (oferta, negociado, acesso) — produtos com schema de precificação vbody
  const productsById = useProductsMap();
  const hasClientPricing = productsById.get(productId)?.integrations?.pricingSchema === 'vbody';
  const [pricingTenantId, setPricingTenantId] = useState<string | null>(null);
  const colCount = 6 + (hasPartnerFeature ? 1 : 0) + (hasClientPricing ? 1 : 0);

  const loadTenants = useCallback(async () => {
    if (!productId?.trim()) {
      setTenants([]);
      setTotal(0);
      setLoadError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      const filters: any = {
        page: page + 1,
        limit: rowsPerPage,
        productId
      };

      if (selectedClients.length > 0) {
        filters.tenantIds = selectedClients;
      }

      const response = await apiService.getTenants(filters);

      if (response.data.success) {
        setTenants(response.data.data);
        setTotal(response.data.pagination.total);
      }
    } catch (error) {
      setTenants([]);
      setTotal(0);
      setLoadError(error);
      logger.error('Error loading tenants:', error);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, selectedClients, productId]);

  useEffect(() => {
    setPage(0);
  }, [selectedClients, productId]);

  useEffect(() => {
    loadTenants();
  }, [loadTenants]);

  useEffect(() => {
    setRefreshCallback(loadTenants);
    return () => setRefreshCallback(null);
  }, [loadTenants, setRefreshCallback]);

  const handleSubscriptionToggle = (tenantId: string, nextActive: boolean) => {
    if (!nextActive) {
      confirm({
        title: 'Desativar assinatura no app',
        message:
          'O cliente pode perder acesso ao aplicativo conforme as regras de inadimplência. Deseja marcar subscriptionActive como falso?',
        confirmText: 'Desativar',
        cancelText: 'Cancelar',
        confirmColor: 'warning',
        onConfirm: async () => {
          try {
            await apiService.patchTenantSubscriptionBilling(tenantId, false);
            showSuccess('Assinatura (app) desativada');
            loadTenants();
          } catch (error: any) {
            showError(error.response?.data?.message || 'Erro ao atualizar assinatura');
          }
        }
      });
      return;
    }
    confirm({
      title: 'Ativar assinatura no app',
      message:
        'Marca subscriptionActive como verdadeiro. O cliente passará a ser validado pelo fluxo de inadimplência (vover-app). Confirma?',
      confirmText: 'Ativar',
      cancelText: 'Cancelar',
      onConfirm: async () => {
        try {
          await apiService.patchTenantSubscriptionBilling(tenantId, true);
          showSuccess('Assinatura (app) ativada');
          loadTenants();
        } catch (error: any) {
          showError(error.response?.data?.message || 'Erro ao atualizar assinatura');
        }
      }
    });
  };

  const handleIsPartnerToggle = (tenantId: string, nextIsPartner: boolean) => {
    confirm({
      title: nextIsPartner ? 'Marcar como cliente parceiro' : 'Remover status de parceiro',
      message: nextIsPartner
        ? 'Cliente parceiro tem acesso permanente ao sistema, sem bloqueio por trial ou inadimplência. Confirma?'
        : 'O cliente voltará ao fluxo padrão de trial e cobrança. Confirma?',
      confirmText: nextIsPartner ? 'Marcar como parceiro' : 'Remover parceiro',
      cancelText: 'Cancelar',
      confirmColor: nextIsPartner ? 'primary' : 'warning',
      onConfirm: async () => {
        try {
          await apiService.patchTenantIsPartner(tenantId, nextIsPartner);
          showSuccess(
            nextIsPartner ? 'Cliente marcado como parceiro' : 'Status de parceiro removido'
          );
          loadTenants();
        } catch (error: any) {
          showError(error.response?.data?.message || 'Erro ao atualizar status de parceiro');
        }
      }
    });
  };

  const handleIsTestToggle = (tenantId: string, nextIsTest: boolean) => {
    confirm({
      title: nextIsTest ? 'Conta de teste (revisão de loja)' : 'Remover conta de teste',
      message: nextIsTest
        ? 'Marca is_test no cadastro do cliente: o vover-app não aplica paywall de trial/fatura (login, app e formulários públicos). Confirma?'
        : 'Remove a marca: passam a valer trial e cobrança como nos demais clientes. Confirma?',
      confirmText: nextIsTest ? 'Marcar' : 'Remover',
      cancelText: 'Cancelar',
      confirmColor: nextIsTest ? 'primary' : 'warning',
      onConfirm: async () => {
        try {
          await apiService.patchTenantIsTest(tenantId, nextIsTest);
          showSuccess(nextIsTest ? 'Conta de teste ativada' : 'Conta de teste desativada');
          loadTenants();
        } catch (error: any) {
          showError(error.response?.data?.message || 'Erro ao atualizar conta de teste');
        }
      }
    });
  };

  const handleStatusToggle = (isActive: boolean, tenantId: string) => {
    const action = isActive ? 'desativar' : 'ativar';
    confirm({
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} Tenant`,
      message: `Tem certeza que deseja ${action} este tenant?`,
      confirmText: action.charAt(0).toUpperCase() + action.slice(1),
      cancelText: 'Cancelar',
      confirmColor: isActive ? 'error' : 'primary',
      onConfirm: async () => {
        try {
          if (isActive) {
            await apiService.deactivateTenant(tenantId);
          } else {
            await apiService.activateTenant(tenantId);
          }
          showSuccess(`Cliente ${!isActive ? 'ativado' : 'desativado'} com sucesso`);
          loadTenants();
        } catch (error: any) {
          showError(error.response?.data?.message || 'Erro ao alterar status do tenant');
        }
      }
    });
  };

  return (
    <ProductScopeGate
      productId={productId}
      integration="appApi"
      loading={loading}
      loadError={loadError}
      isEmpty={false}
      onRetry={loadTenants}
    >
      <Box>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            mb: 1
          }}
        >
          <Typography variant="h4" component="h1">
            {COPY.tenants.title}
          </Typography>
          <Button
            variant="contained"
            disabled={creatingTestTenant || loading || !productId?.trim()}
            onClick={async () => {
              setCreatingTestTenant(true);
              try {
                const res = await apiService.createMobileTestTenants(productId);
                if (res.data?.success) {
                  showSuccess(
                    (res.data as { message?: string }).message ||
                      'Clientes de teste Google e Apple criados/atualizados.'
                  );
                  await loadTenants();
                } else {
                  showError('Não foi possível criar os clientes de teste.');
                }
              } catch (error: any) {
                showError(error.response?.data?.message || 'Erro ao criar clientes de teste');
              } finally {
                setCreatingTestTenant(false);
              }
            }}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            {creatingTestTenant ? 'Criando...' : COPY.tenants.createTestClients}
          </Button>
        </Box>

        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Cliente</TableCell>
                <TableCell>CNPJ / CPF</TableCell>
                <TableCell>E-mail</TableCell>
                <TableCell>Conta</TableCell>
                <TableCell>Assinatura (app)</TableCell>
                <TableCell>Conta teste (loja)</TableCell>
                {hasPartnerFeature && <TableCell>Parceiro</TableCell>}
                {hasClientPricing && <TableCell>Preço</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={colCount} align="center">
                    Carregando...
                  </TableCell>
                </TableRow>
              ) : tenants.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={colCount} align="center">
                    <Alert severity="info" sx={{ textAlign: 'left', display: 'inline-flex' }}>
                      {COPY.tenants.noTenants} A integração com o produto está OK — ainda não há
                      clientes cadastrados neste escopo.
                    </Alert>
                  </TableCell>
                </TableRow>
              ) : (
                tenants.map(tenant => (
                  <TableRow key={tenant._id || tenant.tenantId}>
                    <TableCell>
                      <ClientCell
                        name={tenant.companyName}
                        tenantId={tenant.tenantId}
                        logoUrl={(tenant as any).logoUrl}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{tenant.cnpj || tenant.cpf || '-'}</TableCell>
                    <TableCell>{tenant.contactEmail}</TableCell>
                    <TableCell>
                      <StatusToggle
                        isActive={tenant.isActive}
                        onClick={isActive => handleStatusToggle(isActive, tenant.tenantId)}
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Mesmo campo subscriptionActive do control usado pelo vover-app (inadimplência pode desativar).">
                        <FormControlLabel
                          sx={{ m: 0 }}
                          control={
                            <Switch
                              size="small"
                              checked={tenant.subscriptionActive === true}
                              onChange={(_, c) => handleSubscriptionToggle(tenant.tenantId, c)}
                            />
                          }
                          label={
                            tenant.subscriptionActive === true ? (
                              <Typography variant="body2" component="span">
                                Ativa
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary" component="span">
                                Inativa
                              </Typography>
                            )
                          }
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Revisão Google Play / App Store: ignora trial e paywall público no vover-app (campo is_test).">
                        <FormControlLabel
                          sx={{ m: 0 }}
                          control={
                            <Switch
                              size="small"
                              checked={tenant.is_test === true}
                              onChange={(_, c) => handleIsTestToggle(tenant.tenantId, c)}
                            />
                          }
                          label={
                            tenant.is_test === true ? (
                              <Typography variant="body2" component="span">
                                Sim
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary" component="span">
                                Não
                              </Typography>
                            )
                          }
                        />
                      </Tooltip>
                    </TableCell>
                    {hasPartnerFeature && (
                      <TableCell>
                        <Tooltip title="Parceiro: acesso permanente ao sistema, sem bloqueio por trial ou inadimplência.">
                          <FormControlLabel
                            sx={{ m: 0 }}
                            control={
                              <Switch
                                size="small"
                                checked={tenant.isPartner === true}
                                onChange={(_, c) => handleIsPartnerToggle(tenant.tenantId, c)}
                              />
                            }
                            label={
                              tenant.isPartner === true ? (
                                <Typography variant="body2" component="span">
                                  Sim
                                </Typography>
                              ) : (
                                <Typography variant="body2" color="text.secondary" component="span">
                                  Não
                                </Typography>
                              )
                            }
                          />
                        </Tooltip>
                      </TableCell>
                    )}
                    {hasClientPricing && (
                      <TableCell>
                        <Button size="small" onClick={() => setPricingTenantId(tenant.tenantId)}>
                          Preço do cliente
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50, 100]}
        />
        <TenantPricingDialog tenantId={pricingTenantId} onClose={() => setPricingTenantId(null)} />
      </Box>
    </ProductScopeGate>
  );
};

export default TenantsPage;
